import moment from "moment"
import PropTypes from "prop-types"
import React, { useMemo } from "react"
import { Link } from "react-router-dom"
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table"
import { Badge, Col, Input, Row, Table } from "reactstrap"

const formatDate = val => (val ? moment(val).format("DD MMM YYYY") : "-")

const formatCurrency = val =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val)

const SortIcon = ({ column }) => {
  if (column.disableSortBy) return null
  if (column.isSorted) {
    return column.isSortedDesc ? (
      <i className="bx bx-sort-down ms-1" />
    ) : (
      <i className="bx bx-sort-up ms-1" />
    )
  }
  return <i className="bx bx-sort ms-1 text-muted" />
}

const InvoiceList = ({ invoices, onEdit, onDelete }) => {
  const columns = useMemo(
    () => [
      {
        Header: "#",
        id: "rowIndex",
        disableSortBy: true,
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Invoice Date",
        accessor: "invoiceDate",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "Client Name",
        accessor: "clientName",
      },
      {
        Header: "AI Assist Type",
        accessor: "aiAssistType",
        Cell: ({ value }) => (
          <Badge
            color={value === "AI-Assist-Bundle" ? "primary" : "info"}
            pill
            className="font-size-12 px-2 py-1"
          >
            {value}
          </Badge>
        ),
      },
      {
        Header: "From Date",
        accessor: "fromDate",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "To Date",
        accessor: "toDate",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ value }) => (
          <span className="fw-semibold">{formatCurrency(value)}</span>
        ),
      },
      {
        Header: "Actions",
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-primary"
              title="Edit"
              onClick={e => {
                e.preventDefault()
                onEdit(row.original)
              }}
            >
              <i className="mdi mdi-pencil font-size-18" />
            </Link>
            <Link
              to="#"
              className="text-danger"
              title="Delete"
              onClick={e => {
                e.preventDefault()
                onDelete(row.original)
              }}
            >
              <i className="mdi mdi-delete font-size-18" />
            </Link>
          </div>
        ),
      },
    ],
    [onEdit, onDelete]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    preGlobalFilteredRows,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: invoices,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  return (
    <>
      {/* Search & Page-size controls */}
      <Row className="mb-3 align-items-center">
        <Col sm={5} md={4}>
          <div className="search-box">
            <div className="position-relative">
              <Input
                type="text"
                className="form-control"
                placeholder="Search invoices..."
                value={globalFilter || ""}
                onChange={e => setGlobalFilter(e.target.value || undefined)}
              />
              <i className="bx bx-search-alt search-icon" />
            </div>
          </div>
        </Col>
        <Col sm={7} md={8}>
          <div className="d-flex align-items-center justify-content-end gap-2">
            <span className="text-muted font-size-13">
              {preGlobalFilteredRows.length} record
              {preGlobalFilteredRows.length !== 1 ? "s" : ""}
            </span>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto" }}
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
            >
              {[10, 25, 50].map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table
          {...getTableProps()}
          className="align-middle table-nowrap table-hover mb-0"
        >
          <thead className="table-light">
            {headerGroups.map(hg => (
              <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                {hg.headers.map(col => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={col.id}
                    style={{ whiteSpace: "nowrap", cursor: col.disableSortBy ? "default" : "pointer" }}
                  >
                    {col.render("Header")}
                    <SortIcon column={col} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map(row => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()} key={cell.column.id}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-5 text-muted">
                  <i className="mdi mdi-file-document-outline font-size-36 d-block mb-2" />
                  No invoices found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <Row className="justify-content-between align-items-center mt-3">
        <Col sm={6}>
          <p className="text-muted mb-0">
            Page <strong>{pageIndex + 1}</strong> of{" "}
            <strong>{pageOptions.length || 1}</strong>
          </p>
        </Col>
        <Col sm={6}>
          <ul className="pagination justify-content-end mb-0">
            <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                &laquo;
              </button>
            </li>
            <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={previousPage}
                disabled={!canPreviousPage}
              >
                &lsaquo;
              </button>
            </li>
            <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={nextPage}
                disabled={!canNextPage}
              >
                &rsaquo;
              </button>
            </li>
            <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => gotoPage(pageOptions.length - 1)}
                disabled={!canNextPage}
              >
                &raquo;
              </button>
            </li>
          </ul>
        </Col>
      </Row>
    </>
  )
}

InvoiceList.propTypes = {
  invoices: PropTypes.array.isRequired,
  onEdit:   PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export default InvoiceList
